import React, {
    useRef,
    useLayoutEffect,
    forwardRef,
    useImperativeHandle,
} from 'react';

export interface CanvasRef {
    canvas: HTMLCanvasElement
    clear: () => void
    toDataURL: (format: string, quality?: any) => string
    isEmpty: () => boolean
}

export interface SignatureTextOptions {
    fontSize?: number
    fontFamily?: string
    fontColor?: string
    marginVertical?: number
    marginHorizontal?: number
}

export interface SignatureTextProps {
    text: string
    textOptions?: SignatureTextOptions
}
export const SignatureText = forwardRef<CanvasRef, SignatureTextProps>(({
    text,
    textOptions = {},
}, ref) => {
    const canvas = useRef<HTMLCanvasElement>(null)
    const contextRef = useRef<CanvasRenderingContext2D>(null)

    const clearCanvas = () => {
        const context = contextRef.current
        context.clearRect(0, 0, canvas.current.width, canvas.current.height)
    }

    const prepareFont = () => {
        const { fontSize, fontFamily, fontColor } = textOptions
        const context = contextRef.current

        context.font = `${fontSize || 48}px ${fontFamily || 'Italianno'}`
        context.fillStyle = fontColor || 'black'
    }

    const drawText = () => {
        const { marginHorizontal, marginVertical, fontSize }  = textOptions
        const context = contextRef.current
        clearCanvas()
        prepareFont()

        const textDimensions = context.measureText(text)
        const newWidth = textDimensions.width + (marginHorizontal || 10) * 2
        const newHeight = textDimensions.actualBoundingBoxAscent + textDimensions.actualBoundingBoxDescent + (marginVertical || 10) * 4

        if (newWidth !== context.canvas.width || newHeight !== context.canvas.height) {
            canvas.current.width = newWidth
            canvas.current.height = newHeight
            clearCanvas()
            prepareFont()
        }
        context.fillText(text, (marginHorizontal || 10), (marginVertical || 10) + fontSize)
    }

    useLayoutEffect(() => {
        contextRef.current = canvas.current.getContext('2d')

        function resizeCanvas() {
            const ratio = 1 // Math.max(window.devicePixelRatio || 1, 1);
            canvas.current.width = canvas.current.offsetWidth * ratio;
            canvas.current.height = canvas.current.offsetHeight * ratio;
            canvas.current.getContext("2d").scale(ratio, ratio);
            clearCanvas()
            if (text)
                drawText()
        }

        window.addEventListener('resize', resizeCanvas)
        resizeCanvas()

        return () => {
            window.removeEventListener('resize', resizeCanvas)
        }
    }, [text])

    useLayoutEffect(() => {
        if (canvas.current) {
            drawText()
        }
    }, [
        text, 
        textOptions.fontFamily, 
        textOptions.fontColor, 
        textOptions.fontSize,
    ])

    useImperativeHandle(ref, () => ({
        canvas: canvas.current,
        clear: () => {
            clearCanvas()
        },
        toDataURL: (format: string = 'image/png', quality?: any) => {
            return canvas.current.toDataURL(format, quality)
        },
        isEmpty: () => {
            // https://stackoverflow.com/questions/17386707/how-to-check-if-a-canvas-is-blank
            const context = contextRef.current;

            const pixelBuffer = new Uint32Array(
                context.getImageData(0, 0, canvas.current.width, canvas.current.height).data.buffer
            );

            return !pixelBuffer.some(color => color !== 0);
        }
    }))

    return (
        <canvas ref={canvas} />
    )
})

export default SignatureText
